import React, { useContext, useEffect, useMemo } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Collapse } from '../../components/collapse'
import {
  CollapseTogglePanel,
  WithCollapsePanelContext,
  collapsePanelContext,
} from '../../components/collapse-toggle-panel'
import { renderObjectToDataRows } from '../../helpers/object-to-data-rows'
import { EmulationGraphBase } from './emulation-graph'
import { CollapsibleEmulationItem } from './emulation-item'
import { useExport } from '../../contexts'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { applyFilter } from './helpers'
import { reportFilterContext } from '../../components/filter/report-filter-context'
import { NoFilteredItemsNote } from '../../components/filter/no-items-note'
import { useURLSearchParam } from 'app/modules/shared'
import { actionsCategories } from './action-category'
import { EmulationCategory } from './types'
import { FilterNotification } from '../../components/filter/filter-notification'

/**
 * Display data from `emulationData` report section
 */
type FilesEmulationDataReportProps = {
  data: any
}
export function FileEmulationDataReport(props: FilesEmulationDataReportProps): JSX.Element | null {
  const { _t } = useTypedTranslation()
  const { exporting } = useExport()
  const { data } = props
  const filterContext = useContext(reportFilterContext)
  const filterValues = filterContext.getValues()
  const typeUrlFilter = useURLSearchParam('genericNodeType')
  const items = useMemo(() => {
    return applyFilter(data.data, filterValues)
  }, [data.data, filterValues, typeUrlFilter])

  // Apply overview node type filter, passed via URL
  useEffect(() => {
    if (!typeUrlFilter) {
      return
    }
    let types: string[] = []

    if (typeUrlFilter === 'process') {
      types = ['StartProcess']
    } else if (typeUrlFilter === 'network') {
      types = Object.keys(actionsCategories[EmulationCategory.Network])
    } else if (typeUrlFilter === 'other') {
      Object.keys(actionsCategories).forEach((category) => {
        if (category === EmulationCategory.Network) {
          return
        }
        const actions: { [key: string]: any } = { ...actionsCategories[category as keyof typeof actionsCategories] }
        delete actions.StartProcess
        types = types.concat(Object.keys(actions))
      })
    }

    filterContext.setValues({ type: types })
  }, [data, typeUrlFilter])

  const exitElem = data.exitCode ? (
    exporting ? (
      <i className="fas fa-exclamation-triangle text-warning ms-1" />
    ) : (
      <OverlayTrigger
        placement="auto"
        overlay={
          <Tooltip id="emulation-tooltip">
            <span>{_t('emulation-issue', { exitCode: `(exit code: ${data.exitCode})` })}</span>
          </Tooltip>
        }
      >
        {({ ref, ...triggerHandler }) => (
          <i {...triggerHandler} ref={ref} className="fas fa-exclamation-triangle text-warning ms-1" />
        )}
      </OverlayTrigger>
    )
  ) : null

  return (
    <div>
      <EmulationGraphBase tree={data.tree} />
      {Object.keys(data.meta).length !== 0 && (
        <WithCollapsePanelContext>
          <div className="header-container mb-3">
            <h2 className="h3">{_t('emulation-meta-data')}</h2>
          </div>
          <div className="report-collapses-list-component mb-8">
            <EmulationMetaDataReport data={data.meta} />
          </div>
        </WithCollapsePanelContext>
      )}

      <WithCollapsePanelContext>
        <div className="header-container card-title">
          <h2 className="h3">{_t('actions')}</h2>
          {!exporting && <CollapseTogglePanel />}
        </div>
        <div className="text-muted report-block-top-note">
          <i>{_t('emulation-data-results-note')}</i>
          {exitElem}
        </div>

        <FilterNotification />

        <div className="report-collapses-list-component" key={items.length}>
          {items.map((item, idx) => (
            <CollapsibleEmulationItem item={item} opened={items.length === 1} idx={idx} key={item.dataUUID} />
          ))}
          {!items.length ? <NoFilteredItemsNote /> : null}
        </div>
      </WithCollapsePanelContext>
    </div>
  )
}

/**
 * Build emulation meta data report
 */
type EmulationMetaDataReportProps = {
  data: { [key: string]: any }
}
function EmulationMetaDataReport(props: EmulationMetaDataReportProps) {
  const { data } = props
  const meta = data.Overview
  const { _t } = useTypedTranslation()
  const collapsePanel = useContext(collapsePanelContext)
  const { uniqueIdx } = collapsePanel.getCollapseBlockState(0, false)
  const header = <div>{_t('overview')}</div>

  return <Collapse header={header} body={renderObjectToDataRows(meta)} key={uniqueIdx} />
}
